// src/api/accountabilityApi.ts
//
// Work Accountability RTK Query endpoints (PR-E). Consumes the deployed backend
// (baseUrl is '/api', so paths omit the '/api' prefix). No policy logic here —
// the frontend only reads/writes the existing contracts.

import { baseApi } from './baseApi';
import type {
  AccountabilityPolicy,
  AudienceTarget,
  CompletionHistoryResponse,
  CompletionRecord,
  EffectivePolicyResponse,
  TaskTimeEntry,
  TaskTimeResponse,
  WorkAccountabilityRules,
  WorkScopeTarget,
} from '../types/workAccountability';

export interface CreatePolicyBody {
  name: string;
  rules?: Partial<WorkAccountabilityRules>;
  rulesSchemaVersion?: number;
  lockedFields?: string[];
  audience?: AudienceTarget[];
  workScope?: WorkScopeTarget[];
  isActive?: boolean;
  effectiveAt?: string | null;
  expiresAt?: string | null;
}

export interface UpdatePolicyBody {
  expectedVersion: number;
  name?: string;
  rules?: Partial<WorkAccountabilityRules>;
  lockedFields?: string[];
  effectiveAt?: string | null;
  expiresAt?: string | null;
}

export interface EffectiveQuery {
  userId?: string;
  workItemId?: string;
  projectId?: string;
  assigneeUserId?: string;
}

export const accountabilityApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Policies ──
    getPolicies: builder.query<AccountabilityPolicy[], void>({
      query: () => '/accountability/policies',
      transformResponse: (r: { policies: AccountabilityPolicy[] }) => r.policies ?? [],
      providesTags: (result) =>
        result
          ? [...result.map((p) => ({ type: 'AccountabilityPolicy' as const, id: p.id })), { type: 'AccountabilityPolicy', id: 'LIST' }]
          : [{ type: 'AccountabilityPolicy', id: 'LIST' }],
    }),
    createPolicy: builder.mutation<AccountabilityPolicy, CreatePolicyBody>({
      query: (body) => ({ url: '/accountability/policies', method: 'POST', body }),
      transformResponse: (r: { policy: AccountabilityPolicy }) => r.policy,
      invalidatesTags: [{ type: 'AccountabilityPolicy', id: 'LIST' }, { type: 'AccountabilityEffective', id: 'LIST' }],
    }),
    updatePolicy: builder.mutation<AccountabilityPolicy, { id: string; body: UpdatePolicyBody }>({
      query: ({ id, body }) => ({ url: `/accountability/policies/${id}`, method: 'PATCH', body }),
      transformResponse: (r: { policy: AccountabilityPolicy }) => r.policy,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AccountabilityPolicy', id }, { type: 'AccountabilityPolicy', id: 'LIST' }, { type: 'AccountabilityEffective', id: 'LIST' }],
    }),
    activatePolicy: builder.mutation<AccountabilityPolicy, { id: string; expectedVersion: number }>({
      query: ({ id, expectedVersion }) => ({ url: `/accountability/policies/${id}/activate`, method: 'POST', body: { expectedVersion } }),
      transformResponse: (r: { policy: AccountabilityPolicy }) => r.policy,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AccountabilityPolicy', id }, { type: 'AccountabilityPolicy', id: 'LIST' }, { type: 'AccountabilityEffective', id: 'LIST' }],
    }),
    deactivatePolicy: builder.mutation<AccountabilityPolicy, { id: string; expectedVersion: number }>({
      query: ({ id, expectedVersion }) => ({ url: `/accountability/policies/${id}/deactivate`, method: 'POST', body: { expectedVersion } }),
      transformResponse: (r: { policy: AccountabilityPolicy }) => r.policy,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AccountabilityPolicy', id }, { type: 'AccountabilityPolicy', id: 'LIST' }, { type: 'AccountabilityEffective', id: 'LIST' }],
    }),
    replaceAudience: builder.mutation<AccountabilityPolicy, { id: string; targets: AudienceTarget[]; expectedVersion: number }>({
      query: ({ id, targets, expectedVersion }) => ({ url: `/accountability/policies/${id}/audience`, method: 'PUT', body: { targets, expectedVersion } }),
      transformResponse: (r: { policy: AccountabilityPolicy }) => r.policy,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AccountabilityPolicy', id }, { type: 'AccountabilityPolicy', id: 'LIST' }, { type: 'AccountabilityEffective', id: 'LIST' }],
    }),
    replaceWorkScope: builder.mutation<AccountabilityPolicy, { id: string; targets: WorkScopeTarget[]; expectedVersion: number }>({
      query: ({ id, targets, expectedVersion }) => ({ url: `/accountability/policies/${id}/work-scope`, method: 'PUT', body: { targets, expectedVersion } }),
      transformResponse: (r: { policy: AccountabilityPolicy }) => r.policy,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AccountabilityPolicy', id }, { type: 'AccountabilityPolicy', id: 'LIST' }, { type: 'AccountabilityEffective', id: 'LIST' }],
    }),

    // ── Person rules ──
    getPersonRules: builder.query<AccountabilityPolicy[], { userId?: string } | void>({
      query: (arg) => ({ url: '/accountability/person-rules', params: arg && arg.userId ? { userId: arg.userId } : undefined }),
      transformResponse: (r: { personRules: AccountabilityPolicy[] }) => r.personRules ?? [],
      providesTags: (result) =>
        result
          ? [...result.map((p) => ({ type: 'AccountabilityPersonRule' as const, id: p.id })), { type: 'AccountabilityPersonRule', id: 'LIST' }]
          : [{ type: 'AccountabilityPersonRule', id: 'LIST' }],
    }),
    assignPersonRule: builder.mutation<AccountabilityPolicy, {
      userId: string;
      rules?: Partial<WorkAccountabilityRules>;
      lockedFields?: string[];
      workScope?: WorkScopeTarget[];
      effectiveAt?: string | null;
      expiresAt?: string | null;
      assignmentReason?: string | null;
    }>({
      query: (body) => ({ url: '/accountability/person-rules', method: 'POST', body }),
      transformResponse: (r: { personRule: AccountabilityPolicy }) => r.personRule,
      invalidatesTags: [{ type: 'AccountabilityPersonRule', id: 'LIST' }, { type: 'AccountabilityEffective', id: 'LIST' }],
    }),
    expirePersonRule: builder.mutation<AccountabilityPolicy, { id: string; expectedVersion: number; expiresAt?: string }>({
      query: ({ id, expectedVersion, expiresAt }) => ({ url: `/accountability/person-rules/${id}/expire`, method: 'POST', body: { expectedVersion, ...(expiresAt ? { expiresAt } : {}) } }),
      transformResponse: (r: { personRule: AccountabilityPolicy }) => r.personRule,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'AccountabilityPersonRule', id }, { type: 'AccountabilityPersonRule', id: 'LIST' }, { type: 'AccountabilityEffective', id: 'LIST' }],
    }),

    // ── Effective (resolver-driven live preview) ──
    getEffectivePolicy: builder.query<EffectivePolicyResponse, EffectiveQuery | void>({
      query: (arg) => ({ url: '/accountability/effective', params: arg ?? undefined }),
      providesTags: [{ type: 'AccountabilityEffective', id: 'LIST' }],
    }),

    // ── Task time entries ──
    getTaskTime: builder.query<TaskTimeResponse, string>({
      query: (taskId) => `/tasks/${taskId}/time`,
      providesTags: (_r, _e, taskId) => [{ type: 'TaskTimeWA', id: taskId }],
    }),
    addTaskTime: builder.mutation<TaskTimeEntry, { taskId: string; minutes: number; workDate: string; note?: string | null; userId?: string }>({
      query: ({ taskId, ...body }) => ({ url: `/tasks/${taskId}/time`, method: 'POST', body }),
      transformResponse: (r: { timeEntry: TaskTimeEntry }) => r.timeEntry,
      invalidatesTags: (_r, _e, { taskId }) => [{ type: 'TaskTimeWA', id: taskId }, { type: 'AccountabilityEffective', id: 'LIST' }],
    }),
    correctTimeEntry: builder.mutation<TaskTimeEntry, { id: string; taskId: string; expectedVersion: number; minutes?: number; workDate?: string; note?: string | null; correctionReason?: string | null }>({
      query: ({ id, taskId: _taskId, ...body }) => ({ url: `/time-entries/${id}`, method: 'PATCH', body }),
      transformResponse: (r: { timeEntry: TaskTimeEntry }) => r.timeEntry,
      invalidatesTags: (_r, _e, { taskId }) => [{ type: 'TaskTimeWA', id: taskId }],
    }),
    voidTimeEntry: builder.mutation<TaskTimeEntry, { id: string; taskId: string; expectedVersion: number; reason?: string | null }>({
      query: ({ id, taskId: _taskId, expectedVersion, reason }) => ({ url: `/time-entries/${id}/void`, method: 'POST', body: { expectedVersion, ...(reason ? { reason } : {}) } }),
      transformResponse: (r: { timeEntry: TaskTimeEntry }) => r.timeEntry,
      invalidatesTags: (_r, _e, { taskId }) => [{ type: 'TaskTimeWA', id: taskId }],
    }),

    // ── Completion ──
    completeTaskWa: builder.mutation<{ task: unknown; completion: CompletionRecord | null }, {
      taskId: string;
      completionNote?: string | null;
      varianceExplanation?: string | null;
      finalAdjustmentMinutes?: number | null;
      finalAdjustmentNote?: string | null;
    }>({
      query: ({ taskId, ...body }) => ({ url: `/tasks/${taskId}/complete`, method: 'POST', body }),
      invalidatesTags: (_r, _e, { taskId }) => [
        { type: 'Task', id: taskId },
        { type: 'Task', id: 'LIST' },
        { type: 'TaskCompletion', id: taskId },
        { type: 'TaskTimeWA', id: taskId },
      ],
    }),
    getTaskCompletion: builder.query<CompletionHistoryResponse, string>({
      query: (taskId) => `/tasks/${taskId}/completion`,
      providesTags: (_r, _e, taskId) => [{ type: 'TaskCompletion', id: taskId }],
    }),
  }),
});

export const {
  useGetPoliciesQuery,
  useCreatePolicyMutation,
  useUpdatePolicyMutation,
  useActivatePolicyMutation,
  useDeactivatePolicyMutation,
  useReplaceAudienceMutation,
  useReplaceWorkScopeMutation,
  useGetPersonRulesQuery,
  useAssignPersonRuleMutation,
  useExpirePersonRuleMutation,
  useGetEffectivePolicyQuery,
  useGetTaskTimeQuery,
  useAddTaskTimeMutation,
  useCorrectTimeEntryMutation,
  useVoidTimeEntryMutation,
  useCompleteTaskWaMutation,
  useGetTaskCompletionQuery,
} = accountabilityApi;
